import dayjs from "dayjs";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { TableCellContentDisplayOnHover, tableSimpleSort } from "@similarweb/pro-logic-components";
import { Dropdown, IconButton, Tooltip } from "@similarweb/ui-components";

import type { ISharedKeywordGroup } from "app/common/keyword-research/monitor-lists/monitorKeywordsTypes";
import { Hidden } from "app/components/campaigns/searchEngine/SearchEnginesStyledComponents";
import { translate } from "app/filters/i18nFilter";
import { hasGoogleAds } from "app/pages/keyword-research/keyword-analysis/pages/monitor-lists/utils";
import { hasGoogleAdsImportedKeywordGroupId } from "app/services/keyword-groups/utils/googleAdsImportedKeywordGroups";
import { BaseFlex, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import type { DropdownOnClickProps, TableData } from "./monitorKeywordsStartPageTypes";
import {
    DropdownItemStyled,
    EllipsisDropdownItemStyled,
    FlexTextEllipsis,
    TableCellBoldText,
    Tag,
} from "./styledComponents";

interface TableConfigProps {
    tableRowCount?: number | string;
    handleDropdownClick?: (props: DropdownOnClickProps, row: TableData[number]) => void;
    handleEditKeywordsGroups: (row: TableData[number]) => void;
    onShareClick: (row: TableData[number]) => void;
    sorting: { dataIndex: keyof TableData[number]; sortDirection: "ascend" | "descend" };
}

const tooltipDelay = {
    open: 300,
    close: 0,
};

export const getColumnConfig = ({
    tableRowCount,
    handleDropdownClick,
    handleEditKeywordsGroups,
    onShareClick,
    sorting,
}: TableConfigProps) =>
    [
        {
            dataIndex: "Name",
            sorter: tableSimpleSort("Name"),
            sortOrder: sorting.dataIndex === "Name" ? sorting.sortDirection : undefined,
            title: `${translate("research.homepage.keywordgroups.table.keywordgroup")} (${tableRowCount})`,
            width: "40%",
            render: (value: ISharedKeywordGroup["Name"], row: ISharedKeywordGroup) =>
                TableCellBoldText(
                    <FlexRow
                        gap="8px"
                        alignItems="center"
                        width="100%"
                    >
                        <FlexTextEllipsis>{value}</FlexTextEllipsis>
                        {row.isShared ? <Tag>VIEW ONLY</Tag> : null}
                        {hasGoogleAds() && hasGoogleAdsImportedKeywordGroupId(row.Id) ? (
                            <Tag data-automation={`kwl-google-ads-imported-badge-${row.Id}`}>Google Ads imported</Tag>
                        ) : null}
                    </FlexRow>,
                ),
        },
        {
            dataIndex: "Keywords",
            title: translate("research.homepage.keywordgroups.table.keywords"),
            sorter: (prev: ISharedKeywordGroup, next: ISharedKeywordGroup) =>
                prev?.Keywords?.length - next?.Keywords?.length,
            render: (keywords?: string[]) => keywords?.length,
            sortOrder: sorting.dataIndex === "Keywords" ? sorting.sortDirection : undefined,
            width: "12%",
        },
        {
            dataIndex: "sharedBy",
            title: translate("research.homepage.keywordgroups.table.owner"),
            sorter: tableSimpleSort("sharedBy"),
            render: (sharedBy: string, row: ISharedKeywordGroup) =>
                row.isShared ? (sharedBy ?? "Not Available") : "Me",
            sortOrder: sorting.dataIndex === "sharedBy" ? sorting.sortDirection : undefined,
            width: "20%",
        },
        {
            dataIndex: "LastUpdated",
            title: translate("research.homepage.keywordgroups.table.lastupdated"),
            sorter: (a: ISharedKeywordGroup, b: ISharedKeywordGroup) => dayjs(a.LastUpdated).diff(dayjs(b.LastUpdated)),
            render: (date: string) => dayjs(date).format("MMM DD, YYYY"),
            sortOrder: sorting.dataIndex === "LastUpdated" ? sorting.sortDirection : undefined,
        },
        {
            dataIndex: null,
            render: (_val, row: ISharedKeywordGroup) => {
                return (
                    <BaseFlex
                        flexDirection="row-reverse"
                        // We have logic on row click, this is to prevent actions to trigger redirect from row click
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div>
                            <Dropdown
                                appendTo="#monitor-keyword-groups-table"
                                width={200}
                                onClick={(props: DropdownOnClickProps) => handleDropdownClick?.(props, row)}
                                selectedIds={{}}
                            >
                                <Tooltip
                                    delay={tooltipDelay}
                                    content={translate("global.moreActions")}
                                >
                                    <IconButton
                                        id={`moreActions-${row.Id}`}
                                        iconName="dots-more"
                                        type="flat"
                                        iconSize="20px"
                                        height="34px"
                                        width="34px"
                                    />
                                </Tooltip>
                                <DropdownItemStyled id="openListPage">
                                    {translate("keywordGroups.table.dropdown.openKeywordListPage")}
                                </DropdownItemStyled>
                                <DropdownItemStyled id="openSerpPage">
                                    {translate("keywordGroups.table.dropdown.openSerpPlayersPage")}
                                </DropdownItemStyled>
                                <DropdownItemStyled id="openPagesPage">
                                    {translate("keywordGroups.table.dropdown.openPagesPage")}
                                </DropdownItemStyled>
                                <hr style={{ margin: "0px" }} />
                                <EllipsisDropdownItemStyled
                                    id="share"
                                    iconName="social-share"
                                    disabled={!!row.isShared}
                                >
                                    {translate("global.share")}
                                </EllipsisDropdownItemStyled>
                                <EllipsisDropdownItemStyled
                                    id="edit"
                                    iconName="edit-icon"
                                    disabled={!!row.isShared}
                                >
                                    {translate("global.edit")}
                                </EllipsisDropdownItemStyled>
                                <EllipsisDropdownItemStyled
                                    id="delete"
                                    iconName="delete"
                                    disabled={!!row.isShared}
                                >
                                    {translate("global.delete")}
                                </EllipsisDropdownItemStyled>
                            </Dropdown>
                        </div>
                        <Hidden hidden={!!row.isShared}>
                            <TableCellContentDisplayOnHover>
                                <Tooltip
                                    delay={tooltipDelay}
                                    content={translate("global.editList")}
                                >
                                    <IconButton
                                        iconName="edit-icon"
                                        onClick={() => handleEditKeywordsGroups(row)}
                                        type="flat"
                                        iconSize="20px"
                                        height="34px"
                                        width="34px"
                                        isDisabled={!!row.isShared}
                                    />
                                </Tooltip>
                            </TableCellContentDisplayOnHover>
                            <TableCellContentDisplayOnHover>
                                <Tooltip
                                    delay={tooltipDelay}
                                    content={translate("global.shareList")}
                                >
                                    <IconButton
                                        iconName="social-share"
                                        onClick={() => onShareClick(row)}
                                        type="flat"
                                        iconSize="20px"
                                        height="34px"
                                        width="34px"
                                        isDisabled={!!row.isShared}
                                    />
                                </Tooltip>
                            </TableCellContentDisplayOnHover>
                        </Hidden>
                    </BaseFlex>
                );
            },
            width: 130,
        },
    ] as ColumnsType<TableData[number]>;
